<template>
  <v-card class="pa-0 pb-4 ma-4" color="white">
    <v-card-title>
      {{ productName }}
    </v-card-title>
    <v-card-subtitle>
      {{ serialRange }}
    </v-card-subtitle>
    <v-card-text>
      <v-row>
        <v-col cols="12">
          <!-- DEFINE TABS -->
          <v-tabs
            v-model="tab"
            bg-color="white"
            color="black"
            show-arrows
          >
            <!-- MANUALS TAB -->
            <v-tab value="manuals">
              <v-icon class="mr-2" size="small">mdi-book-open-variant</v-icon>
              {{ $t("documents.manuals.title") }}
            </v-tab>
            <!-- SPARE PARTS TAB -->
            <v-tab value="spareParts" v-if="canReadSpareParts">
              <v-icon class="mr-2" size="small">mdi-cog-outline</v-icon>
              {{ $t("documents.spareParts.title") }}
            </v-tab>
            <!--  TROUBLE SHOOTING TAB -->
            <v-tab value="troubleShooting">
              <v-icon class="mr-2" size="small">mdi-toolbox</v-icon>
              {{ $t("documents.troubleshooting.title") }}
            </v-tab>
            <!-- TECHNICAL DATA TAB -->
            <v-tab value="technicaldata">
              <v-icon class="mr-2" size="small">mdi-information-outline</v-icon>
              {{ $t("documents.technicalData.title") }}
            </v-tab>
            <!-- DIAGRAMS TAB -->
            <v-tab value="diagrams" v-if="isService || isAdmin">
              <v-icon class="mr-2" size="small">mdi-tournament</v-icon>
              {{ $t("documents.diagrams.title") }}
            </v-tab>
            <!-- CE TAB -->
            <v-tab value="CE">
              <v-icon class="mr-2" size="small">mdi-atom</v-icon>
              {{ $t("documents.ce.title") }}
            </v-tab>
          </v-tabs>
        </v-col>
      </v-row>

      <!-- MANUALS -->
      <v-tabs-window v-model="tab">
        <v-tabs-window-item value="manuals">
          <v-container fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="manuals"
                  :headers="headers"
                  :items="manuals"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon> mdi-book-open-variant </v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>

        <!-- SPARE PARTS -->
        <v-tabs-window-item value="spareParts" v-if="canReadSpareParts">
          <v-container
            v-if="spareParts === undefined || !spareParts.length"
            fluid
          >
            <v-alert type="info">
              {{ $t("documents.spareParts.unavailable") }}
            </v-alert>
          </v-container>
          <v-container v-else fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="spareParts"
                  :headers="headers"
                  :items="spareParts"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon>mdi-book-open-variant</v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>

        <!-- TROUBLESHOOTING -->
        <v-tabs-window-item value="troubleShooting">
          <v-container
            v-if="
              (troubleShootings === undefined && !this.showInfo) ||
                (!troubleShootings.length && !this.showInfo)
            "
            fluid
          >
            <v-alert type="info">
              {{ $t("documents.troubleshooting.unavailable") }}
            </v-alert>
          </v-container>
          <v-container v-else-if="this.showInfo" fluid>
            <v-alert type="info">
              {{ $t("documents.troubleshooting.includedInManual") }}
            </v-alert>
          </v-container>
          <v-container v-else fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="troubleShootings"
                  :headers="headers"
                  :items="troubleShootings"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon>mdi-book-open-variant </v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>

        <!-- TECHNICAL DATA -->
        <v-tabs-window-item value="technicaldata">
          <v-container
            v-if="
              (technicalData === undefined && !this.showInfo) ||
                (!technicalData.length && !this.showInfo)
            "
            fluid
          >
            <v-alert type="info">
              {{ $t("documents.technicalData.unavailable") }}
            </v-alert>
          </v-container>
          <v-container v-else-if="this.showInfo" fluid>
            <v-alert type="info">
              {{ $t("documents.technicalData.includedInManual") }}
            </v-alert>
          </v-container>
          <v-container v-else fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="technicalData"
                  :headers="headers"
                  :items="technicalData"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon>mdi-book-open-variant</v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>

        <!-- DIAGRAMS -->
        <v-tabs-window-item value="diagrams">
          <v-container v-if="diagrams === undefined || !diagrams.length" fluid>
            <v-alert type="info">
              {{ $t("documents.diagrams.unavailable") }}
            </v-alert>
          </v-container>
          <v-container v-else fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="diagrams"
                  :headers="headers"
                  :items="diagrams"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon>mdi-book-open-variant</v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>

        <!-- CE -->
        <v-tabs-window-item value="CE">
          <v-container v-if="CE === undefined || !CE.length" fluid>
            <v-alert type="info">
              {{ $t("documents.ce.unavailable") }}
            </v-alert>
          </v-container>
          <v-container v-else fluid px-md-6>
            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  :label="$t('common.filters.filter')"
                  variant="underlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="d-flex align-end">
              <v-col xs="auto" class="mr-0">
                <v-data-table
                  v-if="CE"
                  :headers="headers"
                  :items="CE"
                  :search="search"
                  :custom-filter="searchFilter"
                  :loading="isDownloadingFile"
                  hide-default-footer
                  items-per-page="-1"
                  :sort-by="[{ key: 'name', order: 'asc' }]"
                  @click:row="(event, { item }) => rowClickHandler(item)"
                  hover
                >
                  <template v-slot:[`item.icon`]="{}">
                    <v-icon>mdi-book-open-variant</v-icon>
                  </template>
                  <template v-slot:[`item.action`]="{}">
                    <span>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon size="small" v-bind="props">mdi-download</v-icon>
                        </template>
                        {{ $t("machines.manuals.actions.downloadFile") }}
                      </v-tooltip>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>
  </v-card>
</template>

<script>
import fetchClient from "@/utils/fetchClient"
import { mapActions } from "vuex"
import countryList from "country-list"

export default {
  data() {
    return {
      tab: "manuals",
      search: "",
      manuals: [],
      spareParts: [],
      troubleShootings: [],
      technicalData: [],
      diagrams: [],
      CE: [],
      downloadingFiles: 0,
      filesLoading: [],
      url: ""
    }
  },

  computed: {
    isUser() {
      return this.$store.getters.isUser
    },
    isService() {
      return this.$store.getters.isService
    },
    isAdmin() {
      return this.$store.getters.isAdmin
    },
    canReadSpareParts() {
      return this.$store.getters.canReadSpareParts
    },
    headers() {
      return [
        {
          title: "",
          align: "right",
          sortable: false,
          key: "icon",
          width: 20
        },
        {
          title: this.$t("machines.manuals.dataTable.headers.fileName"),
          align: "left",
          sortable: true,
          key: "name"
        },
        {
          title: this.$t("common.dataTable.headers.actions"),
          align: "right",
          sortable: false,
          key: "action"
        }
      ]
    },
    showInfo() {
      return this.$route.query.includeTroubleshooting === "true"
    },
    productName() {
      let name = this.$route.query.productName
      if (this.revision.length > 3) {
        // Magic 3 since the revision can be 'rev', which makes no sense to include
        name = `${name} / ${this.revision}`
      }
      return name
    },
    serialRange() {
      return this.$route.query.serialRange
    },
    revision() {
      return this.$route.query.revision
    },
    productId() {
      return this.$route.query.productId
    },
    isDownloadingFile() {
      return this.downloadingFiles > 0
    }
  },

  async created() {
    this.fetch(this.productId, this.revision, "Manuals")
    this.fetch(this.productId, this.revision, "Spare_parts")
    this.fetch(this.productId, this.revision, "Troubleshooting")
    this.fetch(this.productId, this.revision, "Technical_data")
    this.fetch(this.productId, this.revision, "Diagrams")
    this.fetch(this.productId, this.revision, "CE")
  },

  methods: {
    ...mapActions(["setError"]),

    // fetch
    async fetch(productId, revision, type) {
      // Remove spaces
      revision = revision.replace(/\s/g, "")

      this.url = `/api/v1/documents/${productId}/${revision}/${type}`

      this.loading = true

      try {
        const files = await fetchClient().getDataOrThrow(this.url)
        let countryCode = ""
        let obj = files
        for (var k in obj) {
          if (obj[k] instanceof Object) {
            files[k].name = files[k].filename
            if (type == "Spare_parts") {
              continue
            } else if (type == "Manuals" || type == "Troubleshooting") {
              countryCode = obj[k].name.substr(obj[k].name.length - 6, 2)
              files[k].name = countryList.getName(countryCode)
            } else {
              files[k].name = files[k].name + "  (Multilanguage)"
            }
          }
        }

        if (type == "Manuals") {
          this.manuals = files
        } else if (type == "Troubleshooting") {
          this.troubleShootings = files
        } else if (type == "Spare_parts") {
          this.spareParts = files
        } else if (type == "Technical_data") {
          this.technicalData = files
        } else if (type == "Diagrams") {
          this.diagrams = files
        } else {
          this.CE = files
        }
      } catch (error) {
        this.setError(error)
      }

      this.loading = false
    },

    async rowClickHandler(clickedItem) {
      if (!clickedItem.locked) {
        this.downloadingFiles += 1
        clickedItem.locked = true

        let errorData = null
        const downloadPath =
          "/api/v1/downloads/" + clickedItem.path + "/" + clickedItem.filename

        try {
          const response = await fetchClient().getResponse(
            "GET",
            downloadPath
          )

          if (response.status === 200) {
            const responseData = await response.arrayBuffer()
            const blob = new Blob([responseData], {
              type: response.headers.get("content-type")
            })

            let link = document.createElement("a")
            link.href = window.URL.createObjectURL(blob)
            link.download = clickedItem.filename
            link.click()
          } else {
            errorData = await fetchClient().getError(response)
          }
        } catch (error) {
          errorData = {
            message: this.$t("machines.manuals.messages.failedToDownload", {
              file: downloadPath
            }),
            causedBy: error.toString(),
            details: error.message
          }
        } finally {
          clickedItem.locked = false
          this.downloadingFiles -= 1

          if (errorData != null) {
            errorData.requestMethod = "GET"
            errorData.requestUri = downloadPath

            const err = {
              response: {
                data: errorData
              }
            }
            this.$store.dispatch("setError", err)
          }
        }
      }
    },
    searchFilter(value, search, wrappedItem) {
      return wrappedItem.raw.name.toLowerCase().includes(search.toLowerCase())
    }
  }
}
</script>
